/**
 * The `yieldCurve` section's data: the Treasury curve's history, the Fed Funds
 * target, and weekly bars for the linked charts (`docs/plans/macro.md`).
 *
 * | | source | state |
 * |---|---|---|
 * | bars (SPY, GLD, TLT, …) | `/ws/qv` through `@qufolio/transport`, `1w` from the daily file | **real** when `VITE_QV_WS_URL` (or the backend override) is set |
 * | Treasury curve | `macro_store` `us.treasury.yield_*`, via admin `GET /macro/series` | **real** when the `macro_series` artifact is built; else the fixture |
 * | Fed Funds target | `macro_store` `us.funding.fed_funds_target_upper`, via `GET /macro/series` | **real** when built; else the hand-copied path |
 *
 * Every answer carries `source: "live" | "fixture"`, and the section badges a
 * panel drawn from a fixture — a mock curve under real bars must never read as
 * the real thing.
 *
 * Nothing here throws: a failed curve load (no server, 403, 503 before the
 * `macro_series` artifact is pulled, an empty series) is the fixture plus `source: "fixture"`;
 * a failed bar load is the symbol missing from `bars`.
 */

import {
  QvWebSocketClient,
  type BulkLoad,
  type TransportClient,
  type TransportError,
  type TransportListeners,
} from "@qufolio/transport";
import { actingAs } from "../lib/act-as";
import { currentPass } from "../lib/pass";
import { idToken } from "../lib/session";
import { TENORS, WEEK_MS, isoDay, utc, type CurveRow, type WeeklyBar } from "../lib/yield-curve";
import { requestHeaders } from "../lib/user";
import { httpBase, wsUrl } from "./backend";

export type DataSource = "live" | "fixture";

export interface CurveAnswer {
  /** The tenors each row's `y` is in — always {@link TENORS} on this client. */
  tenors: readonly string[];
  rows: CurveRow[];
  source: DataSource;
}

export interface StepAnswer {
  steps: { date: string; value: number }[];
  source: DataSource;
}

export interface BarsAnswer {
  bars: Record<string, WeeklyBar[]>;
  source: DataSource;
}

// ── The curve ────────────────────────────────────────────────────────────────

const HTTP_BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");

/** The macro store's id for each of {@link TENORS}, in order (`mv/macro.py` `YIELD_FIELDS`). */
export const CURVE_SERIES_IDS: readonly string[] = [
  "us.treasury.yield_1_month",
  "us.treasury.yield_3_month",
  "us.treasury.yield_1_year",
  "us.treasury.yield_2_year",
  "us.treasury.yield_5_year",
  "us.treasury.yield_10_year",
  "us.treasury.yield_30_year",
];
export const FED_UPPER_SERIES_ID = "us.funding.fed_funds_target_upper";

type SeriesPoints = readonly (readonly [string, number | null])[];

/** `GET /macro/series?ids=` (`app/macro_series/read.py` `series`), by id; null when it cannot be read. */
async function fetchSeries(ids: readonly string[]): Promise<Map<string, SeriesPoints> | null> {
  if (!HTTP_BASE) return null;
  try {
    const res = await fetch(`${HTTP_BASE}/macro/series?ids=${encodeURIComponent(ids.join(","))}`, {
      headers: await requestHeaders(),
    });
    if (!res.ok) return null;
    const j = (await res.json()) as { series?: { id: string; points: SeriesPoints }[] };
    return new Map((j.series ?? []).map((x) => [x.id, x.points ?? []]));
  } catch {
    return null;
  }
}

const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** The tenors' series joined on date into curve rows, oldest first; nulls where a tenor is absent. */
export function curveFromSeries(by: Map<string, SeriesPoints>): CurveRow[] {
  const rows = new Map<string, (number | null)[]>();
  CURVE_SERIES_IDS.forEach((id, i) => {
    for (const [date, v] of by.get(id) ?? []) {
      if (!finite(v)) continue;
      const row = rows.get(date) ?? TENORS.map(() => null);
      row[i] = v;
      rows.set(date, row);
    }
  });
  return [...rows.keys()].sort().map((date) => ({ date, y: rows.get(date)! }));
}

/** A daily target series as steps: the first day and each day the value changes. */
export function stepsFromSeries(points: SeriesPoints): { date: string; value: number }[] {
  const steps: { date: string; value: number }[] = [];
  for (const [date, v] of points) {
    if (!finite(v)) continue;
    if (steps[steps.length - 1]?.value !== v) steps.push({ date, value: v });
  }
  return steps;
}

/**
 * The Treasury curve, daily, oldest first — the macro store's `us.treasury.yield_*`
 * through `GET /macro/series`. The fixture when that cannot be read or is empty.
 */
export async function fetchCurve(): Promise<CurveAnswer> {
  const by = await fetchSeries(CURVE_SERIES_IDS);
  const rows = by ? curveFromSeries(by) : [];
  if (rows.length > 0) return { tenors: TENORS, rows, source: "live" };
  return { tenors: TENORS, rows: fixtureCurve(), source: "fixture" };
}

/**
 * The Fed Funds target, upper bound, as steps — `us.funding.fed_funds_target_upper`
 * through `GET /macro/series`. When that cannot be read, the real target path
 * (macro.md, *Findings: Fed path*), hand-copied, marked `fixture`.
 */
export async function fetchFedFunds(): Promise<StepAnswer> {
  const by = await fetchSeries([FED_UPPER_SERIES_ID]);
  const steps = by ? stepsFromSeries(by.get(FED_UPPER_SERIES_ID) ?? []) : [];
  if (steps.length > 0) return { steps, source: "live" };
  return { steps: FED_FUNDS_UPPER.map(([date, value]) => ({ date, value })), source: "fixture" };
}

// ── Bars ─────────────────────────────────────────────────────────────────────

const QV_WS_URL = wsUrl("/ws/qv", import.meta.env.VITE_QV_WS_URL);

/** Ids well clear of a layout's chart groups — this socket is the section's own. */
const GROUP_ID_BASE = 3_900_000_000;
const LOAD_TIMEOUT_MS = 25_000;

/**
 * Weekly bars for each symbol, the whole daily file resampled by the server.
 *
 * One short-lived socket per call — the section's own, not the layout's chart
 * hub: these are a one-shot load of history with no live tail wanted, and
 * joining the hub would rebuild every chart on the layout each time a lane is
 * switched on (`lib/chart-hub.ts`, *The trade this makes*). A symbol that
 * errors, comes back empty or does not answer before the timeout is simply
 * absent from `bars`; the section draws it as "no data" and may ask again.
 * With no socket configured, the fixture.
 */
export async function fetchWeeklyBars(symbols: readonly string[]): Promise<BarsAnswer> {
  if (symbols.length === 0) return { bars: {}, source: QV_WS_URL ? "live" : "fixture" };
  if (!QV_WS_URL) return { bars: fixtureBars(symbols), source: "fixture" };
  const url = QV_WS_URL;
  const bars = await loadWeeklyBars(symbols, (listeners) =>
    new QvWebSocketClient({ url, authToken: idToken, pass: currentPass, actAs: actingAs, listeners }),
  );
  return { bars, source: "live" };
}

/** The slice of a transport client {@link loadWeeklyBars} drives — a seam for tests. */
export type BarsClient = Pick<TransportClient, "connect" | "request" | "disconnect">;
export type BarsClientFactory = (listeners: Pick<TransportListeners, "onBulkLoad" | "onError">) => BarsClient;

/**
 * Ask for every symbol on one socket and settle each one: an `ok` load with
 * bars, a per-group server error, or the timeout.
 *
 * **A per-group server error comes through `onError`, not as a load.**
 * `QvWebSocketClient.dispatchGroup` turns a group with `status: "error"` into
 * `onError({ source: "server", chartGroupId })` and returns — no `onBulkLoad`
 * ever fires for it — so that group is counted as answered (and failed) here.
 * Ending the whole load on the first error would throw away the symbols still
 * on their way. A socket error with no group is not final either: the client
 * reconnects and replays the request by itself, and the timeout bounds it. A
 * decode error loses the one bundle this request gets, so nothing more is
 * coming and the load ends with what it has.
 */
export async function loadWeeklyBars(
  symbols: readonly string[],
  open: BarsClientFactory,
  timeoutMs = LOAD_TIMEOUT_MS,
): Promise<Record<string, WeeklyBar[]>> {
  const bars: Record<string, WeeklyBar[]> = {};
  if (symbols.length === 0) return bars;
  const idFor = new Map<string, string>(symbols.map((s, i) => [String(GROUP_ID_BASE + i), s]));
  const settled = new Set<string>();
  await new Promise<void>((resolve) => {
    let done = false;
    let client: BarsClient | null = null;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      client?.disconnect();
      resolve();
    };
    const settle = (groupId: string) => {
      if (!idFor.has(groupId) || settled.has(groupId)) return;
      settled.add(groupId);
      if (settled.size >= idFor.size) finish();
    };
    const timer = setTimeout(finish, timeoutMs);
    client = open({
      onBulkLoad: (load: BulkLoad) => {
        const sym = idFor.get(load.chartGroupId);
        if (!sym || settled.has(load.chartGroupId)) return;
        if (load.columns.status === "ok") {
          const got = barsFromColumns(load.columns);
          if (got.length > 0) bars[sym] = got;
        }
        settle(load.chartGroupId);
      },
      onError: (error: TransportError) => {
        if (error.chartGroupId !== undefined) settle(error.chartGroupId);
        else if (error.source === "decode") finish();
      },
    });
    const c = client;
    void c
      .connect()
      .then(() =>
        c.request({
          layoutId: "macro-yieldcurve",
          userId: "demo",
          chartGroups: symbols.map((symbol, i) => ({
            chartGroupId: GROUP_ID_BASE + i,
            symbol,
            frequency: "1w",
            indicators: [],
          })),
        }),
      )
      .catch(finish);
  });
  return bars;
}

/** A decoded load → weekly bars. `x` is epoch seconds (`XAxisKind = "seconds"`). */
export function barsFromColumns(columns: BulkLoad["columns"]): WeeklyBar[] {
  const { x } = columns;
  const o = columns.columns.open ?? [];
  const h = columns.columns.high ?? [];
  const l = columns.columns.low ?? [];
  const c = columns.columns.close ?? [];
  const out: WeeklyBar[] = [];
  for (let i = 0; i < x.length; i++) {
    const close = c[i] ?? NaN;
    const t = x[i] ?? NaN;
    if (!Number.isFinite(close) || !Number.isFinite(t)) continue;
    const or = (v: number | undefined) => (v != null && Number.isFinite(v) ? v : close);
    out.push({
      t: columns.xKind === "seconds" ? t * 1000 : t,
      open: or(o[i]),
      high: or(h[i]),
      low: or(l[i]),
      close,
    });
  }
  return out;
}

// ── Fixtures ─────────────────────────────────────────────────────────────────
//
// Shaped like the mock (`docs/mockups/macro-v2.html`): hand-made keyframes
// 2019→2026 for the curve, interpolated weekly with a little deterministic
// noise. Only the seven stored tenors are kept.

const KEYFRAMES: readonly (readonly [string, readonly number[]])[] = [
  ["2019-01-02", [2.4, 2.42, 2.6, 2.55, 2.52, 2.71, 3.05]],
  ["2019-08-28", [2.1, 2.0, 1.78, 1.5, 1.42, 1.47, 1.95]],
  ["2020-01-15", [1.55, 1.56, 1.55, 1.57, 1.63, 1.8, 2.25]],
  ["2020-03-23", [0.02, 0.05, 0.18, 0.3, 0.5, 0.8, 1.35]],
  ["2020-08-05", [0.08, 0.1, 0.13, 0.12, 0.21, 0.52, 1.19]],
  ["2021-03-31", [0.02, 0.03, 0.07, 0.16, 0.92, 1.74, 2.41]],
  ["2022-01-03", [0.05, 0.08, 0.4, 0.78, 1.37, 1.63, 2.01]],
  ["2022-06-14", [1.3, 1.7, 2.9, 3.4, 3.55, 3.48, 3.4]],
  ["2022-10-21", [3.7, 4.1, 4.65, 4.48, 4.34, 4.22, 4.33]],
  ["2023-03-08", [4.7, 4.95, 5.25, 5.05, 4.35, 3.98, 3.9]],
  ["2023-03-17", [4.4, 4.55, 4.4, 3.85, 3.6, 3.43, 3.65]],
  ["2023-07-03", [5.4, 5.5, 5.4, 4.94, 4.3, 4.05, 4.05]],
  ["2023-10-19", [5.55, 5.6, 5.45, 5.2, 4.97, 4.99, 5.11]],
  ["2024-06-28", [5.5, 5.48, 5.1, 4.71, 4.33, 4.36, 4.51]],
  ["2024-09-18", [4.95, 4.75, 4.0, 3.6, 3.47, 3.7, 4.05]],
  ["2025-01-13", [4.35, 4.35, 4.22, 4.38, 4.53, 4.8, 4.98]],
  ["2025-04-08", [4.38, 4.32, 4.0, 3.75, 3.9, 4.3, 4.75]],
  ["2025-10-30", [4.2, 4.05, 3.65, 3.5, 3.6, 4.05, 4.65]],
  ["2026-10-30", [3.92, 3.86, 3.66, 3.55, 3.68, 3.93, 4.38]],
];

function rng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

function fixtureWeeks(): number[] {
  const t0 = utc(KEYFRAMES[0]![0]);
  const t1 = utc(KEYFRAMES[KEYFRAMES.length - 1]![0]);
  const out: number[] = [];
  for (let t = t0; t < t1; t += WEEK_MS) out.push(t);
  out.push(t1);
  return out;
}

export function fixtureCurve(): CurveRow[] {
  const rnd = rng(21);
  const ts = fixtureWeeks();
  return ts.map((t, n) => {
    let k = 0;
    while (k < KEYFRAMES.length - 2 && utc(KEYFRAMES[k + 1]![0]) < t) k++;
    const [da, ya] = KEYFRAMES[k]!;
    const [db, yb] = KEYFRAMES[k + 1]!;
    const a = utc(da);
    const b = utc(db);
    const f = Math.min(1, Math.max(0, (t - a) / (b - a)));
    const noise = n === ts.length - 1 ? 0 : (rnd() - 0.5) * 0.08;
    const y = ya.map((v, i) => {
      const lerp = v + (yb[i]! - v) * f;
      return Math.max(0.01, +(lerp + noise * (i < 3 ? 0.5 : 1)).toFixed(3));
    });
    return { date: isoDay(t), y };
  });
}

const LANE_POINTS: Record<string, readonly number[]> = {
  SPY: [250, 338, 222, 475, 357, 390, 457, 410, 607, 496, 680, 720],
  QQQ: [150, 237, 170, 400, 260, 300, 383, 345, 525, 423, 620, 660],
  IWM: [133, 168, 100, 225, 170, 175, 197, 163, 240, 185, 245, 255],
  GLD: [121, 150, 140, 170, 153, 183, 184, 186, 245, 277, 370, 390],
  TLT: [121, 145, 165, 148, 93, 106, 95, 83, 92, 90, 90, 93],
  KRE: [50, 57, 35, 71, 63, 43, 47, 41, 64, 52, 62, 64],
};
const LANE_DATES = [
  "2019-01-02", "2020-02-19", "2020-03-23", "2021-12-31", "2022-10-14", "2023-03-17",
  "2023-07-31", "2023-10-27", "2024-12-06", "2025-04-08", "2025-10-30", "2026-10-30",
];

export function fixtureBars(symbols: readonly string[]): Record<string, WeeklyBar[]> {
  const ts = fixtureWeeks();
  const out: Record<string, WeeklyBar[]> = {};
  symbols.forEach((sym, n) => {
    const pts = LANE_POINTS[sym];
    if (!pts) return;
    const rnd = rng(51 + n);
    let nz = 0;
    let prev = pts[0]!;
    out[sym] = ts.map((t, i) => {
      let k = 0;
      while (k < LANE_DATES.length - 2 && utc(LANE_DATES[k + 1]!) < t) k++;
      const a = utc(LANE_DATES[k]!);
      const b = utc(LANE_DATES[k + 1]!);
      const f = Math.min(1, Math.max(0, (t - a) / (b - a)));
      nz = nz * 0.75 + (rnd() - 0.5) * 0.025;
      const pa = pts[k]!;
      const pb = pts[k + 1]!;
      const close = i === ts.length - 1 ? pts[pts.length - 1]! : (pa + (pb - pa) * f) * (1 + nz);
      const open = prev;
      prev = close;
      return {
        t,
        open,
        high: Math.max(open, close) * (1 + rnd() * 0.018),
        low: Math.min(open, close) * (1 - rnd() * 0.018),
        close,
      };
    });
  });
  return out;
}

/** The target range's upper bound, every change since 2019 (real; macro.md *Findings*). */
const FED_FUNDS_UPPER: readonly (readonly [string, number])[] = [
  ["2018-12-20", 2.5], ["2019-08-01", 2.25], ["2019-09-19", 2.0], ["2019-10-31", 1.75],
  ["2020-03-04", 1.25], ["2020-03-16", 0.25], ["2022-03-17", 0.5], ["2022-05-05", 1.0],
  ["2022-06-16", 1.75], ["2022-07-28", 2.5], ["2022-09-22", 3.25], ["2022-11-03", 4.0],
  ["2022-12-15", 4.5], ["2023-02-02", 4.75], ["2023-03-23", 5.0], ["2023-05-04", 5.25],
  ["2023-07-27", 5.5], ["2024-09-19", 5.0], ["2024-11-08", 4.75], ["2024-12-19", 4.5],
  ["2025-09-18", 4.25], ["2025-10-30", 4.0], ["2025-12-11", 3.75], ["2026-09-17", 4.0],
];
